import { useState } from "react";
import { Lock, FileSpreadsheet, Unlock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import ConfirmDialog from "@/components/ConfirmDialog";
import PayrollSummary from "@/components/payroll/PayrollSummary";
import { api, download, errMsg } from "@/lib/api";
import { inr, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

export function DownloadPayroll({ month, testid = "payroll-download-btn", size }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await download(`/payroll/runs/${month}/export`); } catch (e) { toast.error("Download failed"); } finally { setBusy(false); }
  };
  return (
    <Button size={size} className="bg-emerald-700 hover:bg-emerald-800" onClick={go} disabled={busy} data-testid={testid}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Download Finalized Payroll (Excel)
    </Button>
  );
}

function Reopen({ month, label, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!reason.trim()) { toast.error("Enter a reason to reopen"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/runs/${month}/reopen`, { reason });
      toast.success(`Payroll reopened${data.reversed ? `, ${data.reversed} ledger deduction${data.reversed > 1 ? "s" : ""} reversed` : ""}`);
      setOpen(false); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" className="border-amber-500 text-amber-800" onClick={() => setOpen(true)} data-testid="payroll-reopen-btn"><Unlock className="h-4 w-4" /> Reopen / Revise</Button>
      <ConfirmDialog open={open} onOpenChange={setOpen} busy={busy} tone="warning" testid="payroll-reopen-dialog" confirmText="Reopen payroll" onConfirm={go}
        title={`Reopen ${label} payroll?`}
        description="The payroll becomes editable again. Any deductions it recorded in advance ledgers are reversed now and recorded again when you re-finalize. The revised payroll will replace this one.">
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for reopening (required)" data-testid="payroll-reopen-reason" />
      </ConfirmDialog>
    </>
  );
}

export default function PayrollFinalized({ run, isAdmin, onReopened }) {
  const t = run.totals;
  return (
    <div className="space-y-5 fade-up" data-testid="payroll-finalized-view">
      <div className="bg-white rounded-xl border border-emerald-300 p-5 flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-emerald-700 text-sm font-semibold" data-testid="payroll-finalized-badge"><Lock className="h-4 w-4" /> Payroll finalized and locked</div>
          <div className="text-xl font-heading font-bold text-slate-900 mt-1">{run.month_label}</div>
          <div className="text-sm text-slate-500" data-testid="payroll-finalized-meta">
            {run.method === "day" ? "Day-wise" : `Hour-wise · ${run.shift_hours} hours/day`} · Finalized {fmtDateTime(run.finalized_at)} by {run.finalized_by}{run.version > 1 ? ` · Revision ${run.version}` : ""}{run.ledger_posts ? ` · ${run.ledger_posts} ledger deduction${run.ledger_posts > 1 ? "s" : ""}` : ""}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && <Reopen month={run.month} label={run.month_label} onDone={onReopened} />}
          <DownloadPayroll month={run.month} />
        </div>
      </div>
      <PayrollSummary totals={{ employees: run.employees, ...t }} testid="finalized-summary" />
      <div className="bg-white rounded-xl border border-slate-200 overflow-auto max-h-[560px]" data-testid="finalized-table">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
            <tr>{["Employee ID", "Employee Name", "Monthly Salary", "Days", "Hours", "Daily", "Hourly", "Earned", "Deduction", "Final Payable"].map((h, i) => <th key={h} className={`px-3 py-2.5 ${i >= 2 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {run.rows.map((r) => (
              <tr key={r.code} className="border-t border-slate-100" data-testid={`finalized-row-${r.code}`}>
                <td className="px-3 py-2 font-semibold text-blue-800 money">{r.code}</td>
                <td className="px-3 py-2">{r.name}</td>
                <td className="px-3 py-2 text-right money">{inr(r.salary)}</td>
                <td className="px-3 py-2 text-right money">{r.days}</td>
                <td className="px-3 py-2 text-right money">{(run.method === "hour" ? r.hours_used : r.actual_hours) ?? "—"}</td>
                <td className="px-3 py-2 text-right money">{inr(r.daily)}</td>
                <td className="px-3 py-2 text-right money">{r.hourly != null ? inr(r.hourly) : "—"}</td>
                <td className="px-3 py-2 text-right money font-semibold">{inr(r.earned)}</td>
                <td className="px-3 py-2 text-right money text-red-700">{inr(r.deduction)}</td>
                <td className="px-3 py-2 text-right money font-bold text-blue-800" data-testid={`finalized-final-${r.code}`}>{inr(r.final)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
